<template>
    <div class="map-wrap" :class="classObj">
        <mapbox-gl-map
        id="map"
        :accessToken="$cfg.mapboxgl.accessToken"
        :options="getMapOptions()"
        @loaded="mapLoaded"/>
        <overlap-picker
        @select="selectLicence"
        v-model="showOverlapPicker"
        :features="overlapFeatures">
            <template slot="header" slot-scope="slotScope">
                {{ slotScope.numFeatures }} licences at these coordinates
            </template>
            <template slot="listItem" slot-scope="slotScope">
                <v-list-tile-title
                v-text="licenceNumberTemplate(
                        slotScope.feature.properties.nid,
                        lookupPropertyById('term', slotScope.feature.properties.term)
                )" />
                <v-list-tile-sub-title class="text--primary">
                    {{ slotScope.feature.properties.qty | numFormat('0,0.[00]') }} m³/year;
                    {{ lookupPropertyById('ind', slotScope.feature.properties.ind) }}
                </v-list-tile-sub-title>
            </template>
        </overlap-picker>
        <style-switcher v-model="showStyleSwitcher" @switchedStyle="addLayers" />
        <map-save-button v-model="showMapSave" :featuresUrl="mapSourceUrl">
            <template slot="features-headline">Download licences</template>
        </map-save-button>
        <map-legend v-model="showMapLegend" :items="getLegendLayers()" />
        <map-draw />
        <map-sidebar />
    </div>
</template>

<script>
import Vue from 'vue';
import mapboxgl from 'mapbox-gl';
import gql from 'graphql-tag';
import bboxPolygon from '@turf/bbox-polygon';
import within from '@turf/boolean-within';
import { apolloClient } from '@/apollo/apollo';
import * as appColors from '@/functions/appColors';

import * as actions from '@/constants/actions';
import saveMapPosition from '@/mixins/saveMapPosition';
import mapPromiseMixin from '@/mixins/map';
import watershedMixin from '@/mixins/watershedData';
import mapLegend from '@/mixins/mapLegend';
import MapDraw from '@/components/MapDraw';
import overlapPickerMixin from '@/mixins/overlapPicker';
import revealFeatureMixin from '@/mixins/revealFeature';
import lookupMixin from '@/mixins/lookupGeojsonProperties';
import labelLayer from '@/functions/labelLayer';
import waterLayer from '@/functions/waterLayer';
import mapStatePositionMixin from '@/mixins/mapStatePosition';

import MapboxGlMap from '@/components/MapboxGlMap';
import StyleSwitcher from '@/components/MapStyleSwitcher';
import MapSidebar from '@/components/WatershedSidebar';
import MapContextMenu from '@/components/MapContextMenu';
import MapSaveButton from '@/components/MapSaveButton';

export default {
    name: 'WatershedPage',
    components: {
        MapboxGlMap,
        StyleSwitcher,
        MapSidebar,
        MapSaveButton,
        MapDraw,
    },
    mixins: [
        saveMapPosition,
        mapPromiseMixin,
        watershedMixin,
        mapLegend,
        overlapPickerMixin,
        revealFeatureMixin,
        lookupMixin,
        mapStatePositionMixin,
    ],
    data() {
        return {
            map: null,
            mapZoom: 5.8,
            tooltipMarker: null,
            contextMenu: null,
            mapLayersReady: false,
            watershedGeojson: null,
            mapSourceUrl: `${this.$cfg.API_URL}/licences`,
            // map filters
            filters: [
                'network',
                'status',
                'type',
                'purpose',
                'quantity',
            ],
            mapLayers: [],
            showStyleSwitcher: false,
            showMapSave: false,
            queryLocationMarker: null,
        };
    },
    computed: {
        showingSidebar() {
            return this.$store.state.sidebar.show;
        },
        classObj() {
            return [
                {
                    'showing-sidebar': this.showingSidebar,
                },
            ];
        },
        // selected licence feature
        selectedLicence() {
            return this.$store.state.map.selectedLicence;
        },
        // selected watershed
        selectedWatershed() {
            return this.$store.state.map.selectedWatershed;
        },
        filterSettings() {
            return this.$store.state.filterSettings;
        },
    },
    beforeCreate() {
        this.$store.dispatch(actions.ADD_LOADING, 'load watershed page');
        this.$store.dispatch(actions.SET_MAP_SOURCE_DATA_LOADED, false);
        // update the mapLoaded promise in vuex
        const mapLoadedPromise = new Promise((resolve, reject) => {
            this.$on('mapLoaded', (map) => {
                resolve(map);
            });
            this.$on('mapLoadError', (err) => {
                reject(err);
            });
        });
        this.$store.dispatch(actions.SET_MAP_PROMISE, mapLoadedPromise);
    },
    async mounted() {
        // get geojson lookup tables
        // this has to happen before anything can be done with the map/data
        await this.getLookupTables();
        // define map layers array
        this.mapLayers = this.getMapLayersArray();

        // add load event handlers
        // before the sources and layers are added
        await this.addLoadEventHandlers();

        // initialize map sources & layers
        await this.addLayers(this.$cfg.mapboxgl.baseStyle);
        this.mapLayersReady = true;

        // set up map when licence data becomes available
        // @TODO: ensure this happens only once per map load,
        // ...and in order
        await this.initTooltip();
        await this.addLayerEventHandlers();
        await this.addViewEventHandlers();
        // if vuex has a new map state queued
        await this.loadStateFromVuex();
        // initialize map filters
        await this.initFilters();
    },
    beforeRouteLeave(to, from, next) {
        // unload and reset map filters
        this.$store.dispatch(actions.UNLOAD_FILTERS);
        // reset sidebar & switch back to the live list view
        this.$store.dispatch(actions.SHOW_MAP_SIDEBAR, false);
        this.$store.dispatch(actions.SET_SIDEBAR_CONTEXT, 'live');
        // reset sidebar context and selected features
        this.$store.dispatch(actions.SELECT_WATERSHED, null);
        this.$store.dispatch(actions.SELECT_LICENCE, null);
        // reset vuex mapPromise to null (ie, "no map")
        this.$store.dispatch(actions.SET_MAP_PROMISE, null);
        next();
    },
    methods: {
        /**
         * return a string template for the licence number
         * prepended with "Licence number:", etc. as needed
         * @param  {String} licenceNum licence number from geojson properties
         * @param  {String} [term='']  licence term, "long" or "short"
         * @return {String}            display string
         */
        licenceNumberTemplate(licenceNum, term = '') {
            let tooltipTpl = `${licenceNum}`;
            if (term === 'long') {
                tooltipTpl = `Licence number: ${tooltipTpl}`;
            }
            return tooltipTpl;
        },
        /**
         * process any state items queued in the "loadState" module
         * then reset all items to null
         */
        async loadStateFromVuex() {
            const loadState = this.$store.state.loadState;
            const map = await this.mapPromise;

            if (loadState.mapCenter) {
                map.setCenter(loadState.mapCenter);
            }

            if (loadState.mapZoom) {
                map.setZoom(loadState.mapZoom);
            }

            if (loadState.mapFilterSettings) {
                this.$store.dispatch(actions.SET_FILTER_SETTINGS, loadState.mapFilterSettings);
            }

            if (loadState.selectFeature && loadState.selectFeature.type === 'watershed') {
                const { id, coords } = loadState.selectFeature;

                // select licence on this page
                this.$store.dispatch(actions.SELECT_WATERSHED, {
                    id,
                    coords,
                });
                this.$store.dispatch(actions.SHOW_MAP_SIDEBAR, true);
            }

            if (loadState.selectFeature && loadState.selectFeature.type === 'licence') {
                const { id, coords } = loadState.selectFeature;
                this.selectLicence({
                    properties: { id },
                    geometry: { coordinates: coords },
                });
            }

            // clear loadState module
            this.$store.dispatch(actions.RESET_LOADSTATE_ITEMS);
        },
        /**
         * get an array of map layer configs
         * @return {Array} formatted config array
         */
        getMapLayersArray() {
            // marker layer ids (so we can do loop operations on all layers)
            return [
                {
                    type: 'licenceData',
                    sourceId: 'licenceData-data',
                    color: [
                        'match',
                        ['get', 'type'],
                        this.lookupPropertyByValue('type', 'GW'), [
                            'match',
                            ['get', 'term'],
                            this.lookupPropertyByValue('term', 'short'), String(appColors.markerGwShort),
                            this.lookupPropertyByValue('term', 'long'), String(appColors.markerGwLong),
                            /* other */ String(appColors.markerUnknown),
                        ],
                        this.lookupPropertyByValue('type', 'SW'), [
                            'match',
                            ['get', 'term'],
                            this.lookupPropertyByValue('term', 'short'), String(appColors.markerSwShort),
                            this.lookupPropertyByValue('term', 'long'), String(appColors.markerSwLong),
                            /* other */ String(appColors.markerUnknown),
                        ],
                        /* other */ String(appColors.markerUnknown),
                    ],
                    pointLayer: 'licenceData-points',
                    /**
                     * callback for map sourcedata event
                     * @param  {Object} event mapbox gl sourcedata event
                     */
                    sourceCallback: () => {
                        const source = this.map.getSource('licenceData-data');
                        if (source && this.map.isSourceLoaded('licenceData-data')) {
                            this.$store.dispatch(actions.SET_MAP_SOURCE_DATA_LOADED, true);
                        }
                    },
                },
            ];
        },
        /**
         * return an array of all interactive feature layer ids
         * @return {Array} layer id strings
         */
        getInteractiveLayers() {
            const featureLayers = [];
            this.mapLayers.forEach((l) => {
                featureLayers.push(l.pointLayer);
            });
            return featureLayers;
        },
        /**
         * map loaded callback
         * @param  {mapboxgl.Map} map map object
         */
        mapLoaded(map) {
            this.map = map;

            // add navigation controls
            map.addControl(new mapboxgl.NavigationControl({
                // hide rotation control
                showCompass: false,
            }), 'top-left');
            // add scale control
            map.addControl(new mapboxgl.ScaleControl({
                maxWidth: 75,
            }));

            // fulfill promise
            this.$emit('mapLoaded', map);
            this.$store.dispatch(actions.REMOVE_LOADING, 'load watershed page');
        },
        /**
         * initialize tooltip object
         * to be controlled with showTooltip, hideTooltip
         */
        async initTooltip() {
            const map = await this.mapPromise;
            const el = document.createElement('div');
            el.classList.add('tooltip-marker');
            el.classList.add('hidden');

            this.tooltipMarker = new mapboxgl.Marker(el, {
                offset: [72, 0],
            })
            .setLngLat(map.getCenter())
            .addTo(map);
        },
        /**
         * show tooltip with given html and coordinates
         * @param  {mapboxgl.LngLat} coordinates LngLatLike coordinates
         * @param  {String} html        tooltip HTML content
         */
        showTooltip(coordinates, html) {
            const el = this.tooltipMarker.getElement();
            this.tooltipMarker.setLngLat(coordinates);
            el.innerHTML = html;
            el.classList.remove('hidden');
        },
        /**
         * hide the map tooltip
         */
        hideTooltip() {
            const el = this.tooltipMarker.getElement();
            el.classList.add('hidden');
        },
        /**
         * add mapbox sources and layers
         */
        async addPointLayers(layerAbove) {
            this.$store.dispatch(actions.ADD_LOADING, 'add map sources');
            const map = await this.mapPromise;

            // add split-up sources
            this.mapLayers.forEach(({ sourceId }) => {
                // reset map source data loaded var
                this.$store.dispatch(actions.SET_MAP_SOURCE_DATA_LOADED, false);
                map.addSource(sourceId, {
                    type: 'geojson',
                    data: `${this.mapSourceUrl}/all.geojson`,
                    buffer: 0,
                });
            });

            // unclustered points layers
            this.mapLayers.forEach(({ pointLayer, sourceId, color }) => {
                map.addLayer({
                    id: pointLayer,
                    type: 'circle',
                    source: sourceId,
                    paint: {
                        'circle-color': color,
                        // make circles larger as the user zooms in
                        'circle-radius': [
                            'interpolate', ['linear'], ['zoom'],
                            1, 2,
                            18, 6,
                        ],
                        'circle-stroke-width': [
                            'interpolate', ['linear'], ['zoom'],
                            1, 1,
                            18, 2,
                        ],
                        'circle-stroke-color': [
                            'match',
                            ['get', 'st'],
                            this.lookupPropertyByValue('st', 'ACTIVE APPL.'),
                            String(appColors.markerStrokeActiveAppl),
                            this.lookupPropertyByValue('st', 'CURRENT'),
                            String(appColors.markerStroke),
                            /* other */ String(appColors.markerUnknown),
                        ],
                    },
                });

                // move the layer below layerAbove, if specified
                if (layerAbove) {
                    map.moveLayer(pointLayer, layerAbove);
                }
            });

            // add non-interactive layers to highlight the selected marker
            map.addLayer({
                id: 'selected-point-bg',
                type: 'circle',
                source: this.getMapLayersArray()[0].sourceId,
                // a green circle to appear beneath the selected feature
                paint: {
                    'circle-color': String(appColors.markerSelected),
                    // make circles larger as the user zooms in
                    'circle-radius': [
                        'interpolate', ['linear'], ['zoom'],
                        1, 12,
                        18, 16,
                    ],
                    'circle-opacity': 0,
                },
            });
            map.addLayer({
                id: 'selected-point',
                type: 'circle',
                source: this.getMapLayersArray()[0].sourceId,
                // same as the selected feature, but slightly larger
                paint: {
                    'circle-color': this.getMapLayersArray()[0].color,
                    // make circles larger as the user zooms in
                    'circle-radius': [
                        'interpolate', ['linear'], ['zoom'],
                        1, 4,
                        18, 8,
                    ],
                    'circle-stroke-width': 2,
                    'circle-opacity': 0,
                    'circle-stroke-opacity': 0,
                    'circle-stroke-color': [
                        'match',
                        ['get', 'st'],
                        this.lookupPropertyByValue('st', 'ACTIVE APPL.'),
                        String(appColors.markerStrokeActiveAppl),
                        this.lookupPropertyByValue('st', 'CURRENT'),
                        String(appColors.markerStroke),
                        /* other */ String(appColors.markerUnknown),
                    ],
                },
            });

            // re-set highlighted marker (if any)
            const selectedLicenceId = (this.selectedLicence) ? this.selectedLicence.id : null;
            this.updateSelectedMarker(selectedLicenceId);
            this.$store.dispatch(actions.REMOVE_LOADING, 'add map sources');
        },
        /**
         * add study area mask polygon
         * @param  {String}  layerAbove layer id to go stack new layers (e.g. labels)
         */
        async addStudyArea(layerAbove) {
            const map = await this.mapPromise;

            // add study area mask
            map.addSource('kwt-study-area', {
                type: 'geojson',
                data: '/static/kwt_studyarea_split.geojson',
            });
            map.addLayer({
                id: 'kwt-study-area',
                type: 'fill',
                source: 'kwt-study-area',
                paint: {
                    'fill-color': String(appColors.studyAreaMask),
                    'fill-opacity': 0.65,
                },
            });

            // move the layer below layerAbove, if specified
            if (layerAbove) {
                map.moveLayer('kwt-study-area', layerAbove);
            }
        },
        /**
         * add user-defined map sources and layers
         * @param  {String}  styleUrl mapbox style URL (for ordering layers)
         */
        async addLayers(styleUrl) {
            const labelLayerId = labelLayer(styleUrl);
            const waterLayerId = waterLayer(styleUrl);

            // add study area mask [below labels, if any]
            await this.addStudyArea(labelLayerId);
            // add watershed polygon layer
            await this.addWatershedLayer(waterLayerId);
            // update watershed source data
            await this.updateWatershedSource();
            // initialize point layer(s)
            await this.addPointLayers(labelLayerId);
        },
        /**
         * bind event handlers to map features
         */
        async addLayerEventHandlers() {
            const map = await this.mapPromise;

            // change mouse cursor on mouseover
            this.getInteractiveLayers().forEach((layerId) => {
                map.on('mousemove', layerId, () => {
                    map.getCanvas().style.cursor = 'pointer';
                });
                map.on('mouseleave', layerId, () => {
                    map.getCanvas().style.cursor = '';
                });
            });

            // update sidebar on map/marker click
            map.on('click', (ev) => {
                if (this.$store.state.selectingPolygon) return;

                // check for features at click location
                if (ev.point) {
                    const markerFeatures = this.map.queryRenderedFeatures(ev.point, {
                        layers: this.getInteractiveLayers(),
                    });
                    if (markerFeatures.length > 0) {
                        // show the marker disambiguator if there are more than one
                        if (markerFeatures.length > 1) {
                            this.overlapFeatures = markerFeatures;
                            this.showOverlapPicker = true;
                            return;
                        }

                        // if only one feature was clicked, select it
                        this.selectLicence(markerFeatures[0]);
                        return;
                    }
                }

                // if an empty area on the map was clicked, switch to watershed
                const zoom = map.getZoom();
                this.watershedClick(ev.lngLat, zoom);
            });

            // show tooltip on mouseover
            this.mapLayers.forEach(({ pointLayer }) => {
                map.on('mousemove', pointLayer, (ev) => {
                    const feature = ev.features[0];
                    const featureCoords = feature.geometry.coordinates;
                    // show tooltip with some useful properties
                    const term = this.lookupPropertyById('term', feature.properties.term);
                    const tooltipTpl = this.licenceNumberTemplate(feature.properties.nid, term);
                    this.showTooltip(featureCoords, tooltipTpl);
                });
                map.on('mouseleave', pointLayer, () => {
                    this.hideTooltip();
                });
            });

            // track map zoom level
            map.on('zoom', () => {
                this.mapZoom = map.getZoom();
            });
        },
        /**
         * update the map layer paint properties to highlight the selected feature
         * @param  {String}  markerId marker ID to highlight
         */
        async updateSelectedMarker(markerId) {
            const map = await this.mapPromise;
            // show the selected marker in the "selected-point" layer
            map.setPaintProperty('selected-point', 'circle-opacity', [
                'case',
                ['==', ['get', 'id'], markerId],
                1,
                0,
            ]);
            map.setPaintProperty('selected-point', 'circle-stroke-opacity', [
                'case',
                ['==', ['get', 'id'], markerId],
                1,
                0,
            ]);
            // show the green highlight underneath the selected marker
            map.setPaintProperty('selected-point-bg', 'circle-opacity', [
                'case',
                ['==', ['get', 'id'], markerId],
                0.5,
                0,
            ]);
        },
        /**
         * bind listeners for map loading events
         */
        async addLoadEventHandlers() {
            const map = await this.mapPromise;
            // bind event listener to track when the source is loaded
            this.mapLayers.forEach(({ sourceCallback }) => {
                map.on('sourcedata', sourceCallback);
            });
        },
        /**
         * bind event handlers to map view events
         * (zoom, pan, etc.)
         */
        async addViewEventHandlers() {
            const map = await this.mapPromise;

            // update bounding box in vuex
            map.on('moveend', () => this.calculateBounds());
            this.calculateBounds(); // initial run

            // save map position in localstorage
            map.on('moveend', () => {
                this.saveMapPosition('bcwt');
            });

            // save map position in vuex
            map.on('moveend', this.setMapStatePosition);

            // show context menu on right mouse click
            map.on('contextmenu', this.showContextMenu);
        },
        /**
         * calculate the viewport bounding box and update vuex
         */
        async calculateBounds() {
            const map = await this.mapPromise;
            const bounds = map.getBounds().toArray();
            // convert to bbox format for turf
            const bbox = [].concat(...bounds);
            this.$store.dispatch(actions.SET_MAP_BOUNDS, bbox);
        },
        /**
        * create and return a Mapbox GL popup
        * @param  {mapboxgl.LngLat} lngLat     LngLatLike coordinates
        * @param  {Object} vComponent Vue component for the popup contents
        * @param  {Object} propsData  props to pass to the vue component
        * @return {mapboxgl.Popup}            Map popup object
        */
        createMapPopup(lngLat, vComponent, propsData) {
            // create (empty) map popup
            const mapboxPopup = new mapboxgl.Popup({
                closeButton: false, // we'll use a custom close button
            })
            .setLngLat(lngLat)
            .setHTML('<div class="click-popup"></div>')
            .addTo(this.map);

            // manually mount Popup component to the empty map popup
            const popupContentEl = mapboxPopup._content.querySelector('.click-popup'); // eslint-disable-line
            const PopupConstructor = Vue.extend(vComponent);
            const PopupComponent = new PopupConstructor({
                store: this.$store,
                propsData,
            }).$mount(popupContentEl);

            // remove mapbox popup on close event
            PopupComponent.$on('close', () => {
                mapboxPopup.remove();
            });

            // destroy Popup component on popup close
            // (happens any time a map popup is removed
            // including a new popup taking its place
            // and manual close events) (see: "closeOnClick")
            mapboxPopup.on('close', () => {
                PopupComponent.$destroy();
            });

            return mapboxPopup;
        },
        showContextMenu({ lngLat }) {
            if (this.contextMenu) {
                this.contextMenu.remove();
            }
            this.contextMenu = this.createMapPopup(lngLat, MapContextMenu, {
                lngLat,
            });
        },
        /**
         * return (non-reactive) Mapbox GL constructor options
         * @return {Object} map options
         */
        getMapOptions() {
            return {
                style: this.$cfg.mapboxgl.baseStyle,
                center: this.loadMapPosition('bcwt').center || [-116.9, 50.4],
                zoom: this.loadMapPosition('bcwt').zoom || 5.8,
                attributionControl: false,
            };
        },
        /**
         * handle watershed clicks (clicks on the map with no features)
         * @param  {Object}  lngLat object with lat, lng
         * @param  {Number}  zoom   map zoom level
         */
        async watershedClick(lngLat, zoom) {
            window.gtag('event', 'Watershed', { action: 'click', label: 'User clicked to find watershed' });
            const { lng, lat } = lngLat;

            // query for watershed polygon
            this.$store.dispatch(actions.ADD_LOADING, 'query watershed data');
            try {
                const { data } = await apolloClient.query({
                    query: gql`query getWatershed($lat: Float!, $lng: Float!, $searchDistance: Int!) {
                        getWatershed(lat: $lat, lng: $lng, searchDistance: $searchDistance) {
                            data
                            wfi
                            fwa
                            name
                        }
                    }`,
                    variables: {
                        lng,
                        lat,
                        searchDistance: this.getSearchDistance(zoom),
                    },
                });
                this.$store.dispatch(actions.REMOVE_LOADING, 'query watershed data');
                if (data.getWatershed.wfi === 0) {
                    this.$store.dispatch(actions.TRIGGER_TOAST, 'No watershed data is available at these coordinates.');
                    return;
                }

                // update watershed polygon source data (layer will automatically update)
                this.watershedGeojson = data.getWatershed.data;
                await this.updateWatershedSource();

                const { wfi } = data.getWatershed;

                // set query watershed ID in vuex
                this.$store.dispatch(actions.SELECT_WATERSHED, {
                    id: wfi,
                    coords: [lng, lat],
                });
                this.$store.dispatch(actions.SET_SIDEBAR_CONTEXT, 'watershed');
                this.$store.dispatch(actions.SHOW_MAP_SIDEBAR, true);
            } catch (e) {
                // if watershed graphql query fails, display a notice
                this.$store.dispatch(actions.TRIGGER_TOAST, 'Error loading watershed.');
            }
        },
        /**
         * update the watershed polygon source data to this.watershedGeojson
         */
        async updateWatershedSource() {
            const map = await this.mapPromise;
            const polygonGeojson = this.watershedGeojson;

            // update source data (layer will automatically update)
            const source = map.getSource('watershed-polygon');
            source.setData({
                type: 'Feature',
                geometry: polygonGeojson,
            });
        },
        /**
         * return a formatted legend config object
         * @return {Object} map legend configuration
         */
        getLegendLayers() {
            return [
                {
                    title: 'Water Licence (Surface Water)',
                    color: String(appColors.markerSwLong),
                },
                {
                    title: 'Use Approval (Surface Water)',
                    color: String(appColors.markerSwShort),
                },
                {
                    title: 'Water Licence (Groundwater)',
                    color: String(appColors.markerGwLong),
                },
                {
                    title: 'Use Approval (Groundwater)',
                    color: String(appColors.markerGwShort),
                },
                {
                    title: 'Active Application',
                    color: '#FFFFFF',
                    borderColor: String(appColors.markerStrokeActiveAppl),
                },
            ];
        },
        /**
         * set the selected licence feature
         * @param  {Object} licence geojson feature from map query
         */
        selectLicence(licence) {
            window.gtag('event', 'Licence', { action: 'select', label: 'User selected licence' });
            const id = licence.properties.id;
            const coords = licence.geometry.coordinates;
            this.$store.dispatch(actions.SELECT_LICENCE, {
                id,
                coords,
            });
            this.$store.dispatch(actions.SHOW_MAP_SIDEBAR, true);
        },
        async initFilters() {
            const filterOptions = await this.getFilterOptions();
            this.$store.dispatch(actions.SET_FILTER_OPTIONS, filterOptions);
            this.$store.dispatch(actions.SET_LOADED_FILTERS, this.filters);
        },
        async getFilterOptions() {
            this.$store.dispatch(actions.ADD_LOADING, 'get licence filters');
            try {
                const { data } = await apolloClient.query({
                    query: gql`query {
                    getLicenceFilters
                }`,
                });
                return data.getLicenceFilters;
            } catch (ex) {
                this.$store.dispatch(actions.TRIGGER_TOAST, 'Error retrieving licence filters');
            } finally {
                this.$store.dispatch(actions.REMOVE_LOADING, 'get licence filters');
            }
            return [];
        },
        async updateMapFilters() {
            const settings = this.filterSettings;
            const mapboxFilters = ['all'];
            const map = await this.mapPromise;

            let isEmpty = false;
            Object.values(settings).forEach((item) => {
                if (Array.isArray(item) && item.length === 0) {
                    isEmpty = true;
                }
            });
            if (isEmpty) {
                map.setFilter('licenceData-points', ['has', 'any']);
                return;
            }

            // network
            if (settings.network) {
                const expressions = settings.network.map((value) => {
                    const valueId = this.lookupPropertyByValue('net', value);
                    return ['==', ['get', 'net'], valueId];
                });
                if (expressions.length > 0) {
                    mapboxFilters.push([
                        'any',
                        ...expressions,
                    ]);
                } else { // if no options are selected
                    // something to select 0 features
                    mapboxFilters.push(['has', 'net']);
                }
            }

            // status
            if (settings.status) {
                const expressions = settings.status.map((value) => {
                    const valueId = this.lookupPropertyByValue('st', value);
                    return ['==', ['get', 'st'], valueId];
                });
                if (expressions.length > 0) {
                    mapboxFilters.push([
                        'any',
                        ...expressions,
                    ]);
                } else { // if no options are selected
                    // something to select 0 features
                    mapboxFilters.push(['!has', 'st']);
                }
            }

            // "type" and "term" (combined)
            if (settings.type) {
                const expressions = settings.type.map((value) => {
                    const [type, term] = value.split(' ');
                    const typeId = this.lookupPropertyByValue('type', type);
                    const termId = this.lookupPropertyByValue('term', term);
                    const typeExpression = ['==', ['get', 'type'], typeId];
                    const termExpression = ['==', ['get', 'term'], termId];
                    return ['all', typeExpression, termExpression];
                });
                if (expressions.length > 0) {
                    mapboxFilters.push([
                        'any',
                        ...expressions,
                    ]);
                } else { // if no options are selected
                    // something to select 0 features
                    mapboxFilters.push(['!has', 'type']);
                }
            }

            // "purpose"
            if (settings.purpose) {
                const expressions = settings.purpose.map((value) => {
                    const valueId = this.lookupPropertyByValue('ind', value);
                    return ['==', ['get', 'ind'], valueId];
                });
                if (expressions.length > 0) {
                    mapboxFilters.push([
                        'any',
                        ...expressions,
                    ]);
                } else { // if no options are selected
                    // something to select 0 features
                    mapboxFilters.push(['!has', 'ind']);
                }
            }

            // "quantity"
            if (settings.quantity) {
                // feature[yearMax] >= filter[yearMin]
                const expressions = settings.quantity.map((value) => {
                    const range = value.split('-');
                    const [min, max] = range.map(v => Number(v));
                    return [
                        'all',
                        // always filter out nulls if a value is set
                        ['!=', null, ['get', 'qty']],
                        [
                            '>=',
                            [
                                'to-number', // cast to number
                                ['get', 'qty'],
                            ],
                            min,
                        ],
                        [
                            '<=',
                            [
                                'to-number', // cast to number
                                ['get', 'qty'],
                            ],
                            max,
                        ],
                    ];
                });
                if (expressions.length > 0) {
                    mapboxFilters.push([
                        'any',
                        ...expressions,
                    ]);
                } else { // if no options are selected
                    // something to select 0 features
                    mapboxFilters.push(['!has', 'qty']);
                }
            }

            map.setFilter('licenceData-points', mapboxFilters);
        },
        /**
         * hide all map tools, optionally skipping one
         * to leave it open
         * @param  {String} skip variable name to skip
         */
        hideMapTools(skip) {
            let vars = [
                'showMapLegend',
                'showStyleSwitcher',
            ];
            if (skip) {
                vars = vars.filter(e => e !== skip);
            }
            vars.forEach((e) => {
                this[e] = false;
            });
        },
        /**
         * select a watershed by moving the map to a location and firing a click event
         */
        async selectWatershed(wfi, coords) {
            const map = await this.mapPromise;
            const lngLat = mapboxgl.LngLat.convert(coords);

            try {
                const { data } = await apolloClient.query({
                    query: gql`query getWatershed($wfi: Int!) {
                        getWatershedWFI(wfi: $wfi) {
                            name
                            fwa
                            data
                        }
                    }`,
                    variables: {
                        wfi,
                    },
                });

                // update watershed polygon source data (layer will automatically update)
                this.watershedGeojson = data.getWatershedWFI.data;
                await this.updateWatershedSource();

                // add/update query location map marker
                this.updateQueryLocationMarker(lngLat);

                // move the map if the feature is outside be viewport
                const bbox = this.$store.state.map.bbox;
                const mapBounds = bboxPolygon(bbox);
                const { lng, lat } = lngLat;
                const featureGeojson = {
                    type: 'Feature',
                    properties: {},
                    geometry: {
                        type: 'Point',
                        coordinates: [
                            lng,
                            lat,
                        ],
                    },
                };

                if (!within(featureGeojson, mapBounds)) {
                    // animate to these coordinates
                    // @IDEA: maybe we can just get the coords from the bbox of the polygon?
                    map.flyTo({
                        center: lngLat,
                        zoom: 14,
                    });
                }
            } catch (e) {
                this.$store.dispatch(actions.TRIGGER_TOAST, 'Error loading watershed data');
            }
        },
        /**
         * add or update watershed query location marker element
         * @param  {LngLat}  [coords=null] query location coordinates
         */
        async updateQueryLocationMarker(coords = null) {
            // remove the marker if coords = null
            if (coords === null) {
                if (this.queryLocationMarker) {
                    this.queryLocationMarker.remove();
                }
                this.queryLocationMarker = null;
                return;
            }

            // create marker element if it doesn't exist
            if (this.queryLocationMarker === null) {
                const map = await this.mapPromise;
                const el = document.createElement('div');
                el.className = 'marker query-location';
                el.style.backgroundImage = 'url(/static/marker-icon-orange.svg)';
                el.style.width = '45px';
                el.style.height = '53px';

                // show sidebar on click
                el.addEventListener('click', (ev) => {
                    ev.stopPropagation();
                    this.$store.dispatch(actions.SET_SIDEBAR_CONTEXT, 'watershed');
                    this.$store.dispatch(actions.SHOW_MAP_SIDEBAR, true);
                });

                // show tooltip on mouseover
                el.addEventListener('mousemove', () => {
                    const markerCoords = this.queryLocationMarker.getLngLat();
                    this.showTooltip(markerCoords, 'Watershed query location');
                });
                el.addEventListener('mouseleave', () => {
                    this.hideTooltip();
                });

                // add marker to map
                this.queryLocationMarker = new mapboxgl.Marker(el, {
                    // @TODO: offset this correctly. can't find the API docs for v0.44.2
                    offset: [-0, -10],
                })
                .setLngLat(coords)
                .addTo(map);
            }

            // update marker location
            if (coords) {
                this.queryLocationMarker.setLngLat(coords);
            }
        },
    },
    watch: {
        // hide tooltip on zoom
        mapZoom() {
            this.hideTooltip();
        },
        // focus on newly-selected licences
        selectedLicence(licence) {
            if (licence) {
                this.revealFeature(licence);
                this.updateSelectedMarker(licence.id);
            }
        },
        // focus on newly-selected watersheds
        selectedWatershed(watershed) {
            if (watershed) {
                const { id, coords } = watershed;
                this.selectWatershed(id, coords);
            } else {
                // if selection is cleared
                this.updateQueryLocationMarker(null);
                this.watershedGeojson = null;
                this.clearWatershedPolygon();
            }
        },
        // update mapbox filters when filter settings change
        filterSettings() {
            this.updateMapFilters();
        },
        // when a map tool is shown, hide all the others
        showMapLegend(val) {
            if (val) {
                this.hideMapTools('showMapLegend');
            }
        },
        // when a map tool is shown, hide all the others
        showStyleSwitcher(val) {
            if (val) {
                this.hideMapTools('showStyleSwitcher');
            }
        },
    },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.map-wrap {
    height: 100%;
    display: flex;
    position: relative;
}

#map-1 {
    height: 100%;
}
</style>

<style lang="scss">
@import '~@/assets/globals.scss';

.tooltip-marker {
    opacity: 1;
    transition: opacity 150ms;
    width: 120px;
    z-index: 2;
    background: rgba(28, 28, 28, .65);
    border-radius: .25rem;
    padding: .5rem .75rem;
    color: #eee;
    line-height: 1.2;

    &.hidden {
        opacity: 0;
    }
}

// strip styles from popup container
// (so we can mount v-cards on the element)
.mapboxgl-popup-content {
    background: none;
    box-shadow: none;
    padding: 0;
}

// move map control areas when the sidebar is showing
.showing-sidebar {
    .mapboxgl-ctrl-bottom-right,
    .mapboxgl-ctrl-top-right {
        right: $sidebar-width; // width of the sidebar
    }
}

.marker {
    &.query-location {
        cursor: pointer;
    }
}
</style>



// WEBPACK FOOTER //
// src/pages/Watershed.vue